<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, nextTick, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import type { Task } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from '@ui/components/dropdown-menu';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import TasksAssigneeSelect from '@/components/tasks/TasksAssigneeSelect.vue';
  import { useTaskCollectionStore } from '@/store';

  interface Props {
    task?: Task;
  }

  const props = defineProps<Props>();
  const taskCollectionStore = useTaskCollectionStore();
  const route = useRoute();
  const open = ref(false);
  const isTooltipDisabled = ref(true);

  const { currentTask } = storeToRefs(taskCollectionStore);

  const componentTask = computed(() => props.task ?? currentTask.value);
</script>

<template>
  <Popover
    :open="open"
    @update:open="
      (value) => {
        open = value;

        if (open) {
          nextTick(() => (isTooltipDisabled = !value));
        } else {
          isTooltipDisabled = true;
        }
      }
    "
  >
    <PopoverTrigger as-child>
      <Button class="shrink-0 px-2.5 gap-x-1.5 group" variant="outline" size="sm">
        <i
          class="fa-layer-group"
          :class="componentTask?.openCompletedRelatedTasks?.length ? 'fas text-primary' : 'far'"
        ></i>

        <strong v-if="componentTask?.openCompletedRelatedTasks?.length" class="text-primary-active">
          {{ componentTask?.openCompletedRelatedTasks?.length }}
        </strong>
      </Button>
    </PopoverTrigger>

    <PopoverContent class="w-[350px] space-y-2.5" side="top">
      <div>
        <h4 class="text-base font-bold">Related Tasks</h4>
        <p v-if="componentTask?.openCompletedRelatedTasks?.length" class="text-muted-foreground">
          All related open or completed tasks
        </p>

        <p v-if="!componentTask?.openCompletedRelatedTasks?.length">
          No open or completed tasks related to this task
        </p>
      </div>

      <div>
        <template v-for="rtask in componentTask?.openCompletedRelatedTasks" :key="rtask?.id">
          <div class="flex items-center truncate gap-x-2.5 h-10">
            <div class="flex items-center h-6">
              <i
                class="text-lg!"
                :class="
                  rtask?.isCompleted
                    ? 'fas fa-check-circle text-success'
                    : 'far fa-check-circle text-muted-foreground'
                "
              ></i>
            </div>

            <router-link
              class="flex-1 truncate"
              :to="{ query: { ...route.query, task_drawer_id: rtask?.id } }"
            >
              <!-- TODO: z-index issue -->
              <Tooltip :delay-duration="250">
                <TooltipTrigger class="truncate w-full text-left">
                  {{ rtask.label }}
                </TooltipTrigger>

                <TooltipContent>
                  {{ rtask.label }}
                </TooltipContent>
              </Tooltip>
            </router-link>

            <TasksAssigneeSelect size="xs" :task="rtask" :show-avatar-only="true" />

            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button variant="ghost" size="icon" class="size-6">
                  <i class="fa-solid fa-ellipsis-vertical"></i>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent class="z-200">
                <DropdownMenuGroup>
                  <router-link
                    class="flex-1 truncate"
                    :to="{
                      query: { ...route.query, task_drawer_id: rtask?.id },
                    }"
                  >
                    <DropdownMenuItem class="flex items-center cursor-pointer gap-4">
                      <i class="fa-solid fa-expand"></i>
                      <span>View task</span>
                    </DropdownMenuItem>
                  </router-link>

                  <DropdownMenuItem class="flex items-center cursor-pointer gap-4">
                    <i class="fa-regular fa-circle-xmark text-destructive"></i>
                    <span>Dismiss Task</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem class="flex items-center cursor-pointer gap-4">
                    <i class="fa-regular fa-flag text-agent"></i>
                    <span>Mark Task Innaccurate</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </template>
      </div>
    </PopoverContent>
  </Popover>
</template>

<style scoped></style>
